import { Box, Search, Settings, LogOut, CheckCircle2 } from "lucide-react";
import { useSupabaseAuth } from "@/contexts/SupabaseAuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { useEffect, useRef, useState } from "react";
import { motion } from 'framer-motion';
import { useCurrentProfile } from "@/hooks/useSupabaseProfile";

const modules = [
  {
    key: "packages",
    title: "Track Deliveries",
    description: "Manage guest packages and deliveries.",
    icon: <Box className="h-8 w-8 text-purple-500" />,
    color: "hover:ring-purple-400 focus:ring-purple-400 border-purple-200",
    shortcut: "⌘ + 1",
    to: "/packages",
    disabled: false,
  },
  {
    key: "lostfound",
    title: "Track Lost Items",
    description: "Log, store, and return lost and found objects.",
    icon: <Search className="h-8 w-8 text-blue-500" />,
    color: "hover:ring-blue-400 focus:ring-blue-400 border-blue-200",
    shortcut: "⌘ + 2",
    to: "/lost-found",
    disabled: false,
  },
  {
    key: "admin",
    title: "Admin Settings (Under construction)",
    description: "Manage users, roles, and system settings.",
    icon: <Settings className="h-8 w-8 text-teal-500" />,
    color: "hover:ring-teal-400 focus:ring-teal-400 border-teal-200",
    shortcut: "⌘ + 3",
    to: "/admin",
    disabled: false,
  },
];

const ONBOARDING_KEY = "desky_onboarding_seen";
const LAST_MODULE_KEY = "desky_last_module";

const Dashboard = () => {
  const { user, logout } = useSupabaseAuth();
  const navigate = useNavigate();
  const [showTooltip, setShowTooltip] = useState(false);
  const [lastModule, setLastModule] = useState<string | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const { data: userProfile } = useCurrentProfile(user?.id);

  // On mount: check onboarding and last module
  useEffect(() => {
    if (!localStorage.getItem(ONBOARDING_KEY)) {
      setShowTooltip(true);
    }
    setLastModule(localStorage.getItem(LAST_MODULE_KEY));
  }, []);

  // Dismiss tooltip
  const dismissTooltip = () => {
    setShowTooltip(false);
    localStorage.setItem(ONBOARDING_KEY, "1");
  };

  // Handle card click
  const handleModuleClick = (mod: typeof modules[0]) => {
    if (!mod.disabled) {
      localStorage.setItem(LAST_MODULE_KEY, mod.key);
      navigate(mod.to);
    }
  };

  // Animation variants for Framer Motion
  const cardVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: 0.1 + i * 0.08, type: "spring", stiffness: 120 } }),
  };

  // Responsive/tap target classes
  const cardBase =
    "group w-full flex items-center gap-4 rounded-xl border bg-white/90 px-6 py-6 shadow-md transition-all duration-150 focus:outline-none focus:ring-2 focus:ring-offset-2 cursor-pointer select-none min-h-[5.5rem] md:min-h-[6.5rem] text-left";
  const cardHover =
    "hover:scale-[1.03] hover:shadow-2xl hover:z-10 focus:scale-[1.03] active:scale-95";
  const cardDisabled = "opacity-60 cursor-not-allowed";

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-gray-900 to-purple-700 overflow-x-hidden">
      {/* Dark overlay behind logo */}
      <div className="absolute inset-0 z-0 bg-gradient-to-b from-black/60 to-transparent pointer-events-none" />
      {/* User context top right */}
      <div className="absolute top-0 right-0 p-4 flex items-center space-x-4 z-10">
        <span className="text-sm text-white/80">
          Logged in as: {user?.user_metadata?.username || user?.email}
        </span>
        <Button variant="ghost" size="sm" onClick={logout} className="text-white hover:bg-white/10">
          <LogOut className="h-4 w-4 mr-2" />
          Logout
        </Button>
      </div>
      {/* Centered card group */}
      <div className="relative z-10 w-full max-w-2xl mx-auto flex flex-col items-center px-4">
        {/* Logo and welcome */}
        <div className="flex flex-col items-center mb-8">
          <img src="/logo-darkBG.png" alt="Desky Logo" className="h-20 w-auto mb-2 drop-shadow-lg" />
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-1">Welcome</h1>
          <p className="text-lg text-white/80 mb-2">Track. Deliver. Simplify.</p>
          <p className="text-base text-white/70 mb-2">Choose a module to get started. You can always switch later.</p>
        </div>
        {/* Onboarding Tooltip */}
        {showTooltip && (
          <div ref={tooltipRef} className="relative w-full flex justify-center mb-4 z-20">
            <div className="bg-white/95 border border-purple-200 shadow-lg rounded-lg px-4 py-3 text-purple-900 text-base font-medium flex items-center gap-2 animate-fade-in-up">
              <span>Start by selecting the module you'll be working in. You can switch any time!</span>
              <button onClick={dismissTooltip} className="ml-2 text-purple-600 hover:text-purple-800 text-lg font-bold focus:outline-none">×</button>
            </div>
            <div className="absolute left-1/2 -bottom-2 -translate-x-1/2 w-4 h-4 bg-white/95 border-l border-b border-purple-200 rotate-45 z-10" />
          </div>
        )}
        {/* Module cards */}
        <div className="w-full flex flex-col gap-4 md:gap-6">
          {modules.map((mod, i) => {
            if (mod.key === "admin" && !userProfile?.is_admin) {
              return null;
            }
            const isLast = lastModule === mod.key;
            return (
              <motion.button
                key={mod.key}
                className={
                  cardBase +
                  " " +
                  (mod.disabled ? cardDisabled : cardHover) +
                  " " +
                  mod.color +
                  (isLast && !mod.disabled ? " ring-2 ring-purple-400 border-purple-400 shadow-lg relative" : "")
                }
                style={{ position: "relative" }}
                onClick={() => handleModuleClick(mod)}
                disabled={mod.disabled}
                tabIndex={mod.disabled ? -1 : 0}
                initial="hidden"
                animate="visible"
                variants={cardVariants}
                custom={i}
                whileHover={!mod.disabled ? { scale: 1.04, boxShadow: "0 8px 32px 0 rgba(80,0,180,0.10)" } : undefined}
                whileTap={!mod.disabled ? { scale: 0.97 } : undefined}
              >
                <div className="flex-shrink-0 relative">
                  {mod.icon}
                  {isLast && !mod.disabled && (
                    <>
                      <CheckCircle2 className="absolute -top-2 -right-2 h-6 w-6 text-purple-400 bg-white rounded-full shadow" />
                      <span className="sr-only">Last visited</span>
                    </>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <span className={`text-xl font-bold ${mod.disabled ? "text-gray-400" : "text-gray-900"}`}>{mod.title}</span>
                  <span className={`block text-base ${mod.disabled ? "text-gray-400" : "text-gray-600"}`}>{mod.description}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
      {/* CSS animation fallback for tooltip */}
      <style>{`
        @keyframes fade-in-up {
          from { opacity: 0; transform: translateY(24px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in-up { animation: fade-in-up 0.5s cubic-bezier(.4,0,.2,1) both; }
      `}</style>
    </div>
  );
};

export default Dashboard;
